import { SplitButton } from '@progress/kendo-react-buttons';
import CustomTitleBar from 'src/components/GuideMeTitleBar';
import Button from 'src/components/design-system/Button';
import {
  CanImportMultipleCompanies,
  CanImportSingleCompany,
} from 'src/components/guards/PermissionsList';
import usePermission from 'src/hooks/usePermissions';
import useViewCheckbook from 'src/hooks/useViewCheckbook';
import { informationIcon } from 'src/utils/icons';

const BasicPageNavigation = () => {
  const { hasEditCheckbookPermission } = useViewCheckbook();
  return (
    <ul className="px-4 font-bold text-[13px]">
      {hasEditCheckbookPermission ? (
        <>
          <li className="list-disc font-semibold">
            Click on 'Period View’ to display Budget, Forecast and Transaction details for one
            Accounting Period.
          </li>
          <li className="list-disc font-semibold">
            Use the Filter pick-list to switch to views of Active, Inactive, and Deleted GL Codes.
          </li>
          <li className="list-disc font-semibold">
            Click
            <Button color="primary" className="inline-block" readonly={true}>
              Edit
            </Button>
            to modify the GL Code description, Fixed GL Code flag, or Active GL Code flag, Budget or
            the Forecast Amount for the current period or any future periods.
          </li>
          <li className="list-disc font-semibold">
            Click on the
            <Button color="success" className="inline-block" readonly={true}>
              Add New GL Code
            </Button>
            to add a new GL Code for selected company.
          </li>
          <li className="list-disc font-semibold">
            Use the checkbox column to select eligible GL Codes for Deletion, and click the Delete
            button to delete in bulk.
          </li>
          <li className="list-disc font-semibold">
            Click
            <SplitButton
              style={{ borderRadius: 0, fontWeight: 400 }}
              fillMode={'flat'}
              className="bg-info !m-1 !text-white pointer-events-none"
              text="Copy Across"
              size="small"
            />
            to copy the Budget or Forecast Amount contained in the first editable column to all
            other editable columns on the row.
          </li>
        </>
      ) : (
        <>
          <li className="list-disc font-semibold">
            Click on 'Period View’ to display Forecast and Transaction details for one Accounting
            Period.
          </li>
          <li className="list-disc font-semibold">
            Use the Filter pick-list to switch to views of Active, Inactive, and Deleted GL Codes.
          </li>
        </>
      )}
    </ul>
  );
};

const ViewingData = () => (
  <ul className="px-4 font-bold text-[13px]">
    <li className="list-disc font-semibold">
      Choose a Fiscal Year in the 'Select a Fiscal Year' drop down OR use the next or previous arrow
      buttons to change Accounting Periods.
    </li>
    <li className="list-disc font-semibold">
      Choose a Department from the ‘Select a Department’ drop down to view Accounting Data for a
      specific department.
    </li>
    <li className="list-disc font-semibold">
      <span className="inline-flex items-center">
        Click on <span className="mx-3">{informationIcon}</span>
      </span>
      in the Departments column to view the department(s) associated with the GL Code for that row.
    </li>
  </ul>
);

const TransactionDetails = ({ isImportable }: { isImportable: boolean }) => (
  <ul className="px-4 font-bold text-[13px]">
    <li className="list-disc font-semibold">
      Click on the
      <Button color="secondary" className="inline-block" readonly={true}>
        View All Logs
      </Button>
      to view all Transactions for all GL Codes associated for the currently selected Department(s)
      and Fiscal Year.
    </li>
    <li className="list-disc font-semibold">
      Use the 'Export' option to export data based on applied filters for the current page, or for
      all pages, to an excel file.
    </li>
    {isImportable && (
      <>
        <li className="list-disc font-semibold">
          Click on the{' '}
          <Button color="secondary" className="inline-block" readonly={true}>
            Import/Export
          </Button>{' '}
          to use Checkbook Import/Export tool.
        </li>
        <li className="list-disc font-semibold">
          Use Import tab in Checkbook Import/Export tool to import GL code data using .xls or .txt
          files
        </li>
      </>
    )}
  </ul>
);

export const useGuideMeSteps = () => {
  const { hasEditCheckbookPermission } = useViewCheckbook();
  const isImportable = usePermission({
    permissions: [...CanImportMultipleCompanies, ...CanImportSingleCompany],
    or: true,
  });

  return [
    {
      content: <BasicPageNavigation />,
      title: (
        <CustomTitleBar
          title={
            hasEditCheckbookPermission
              ? 'Basic Page Navigation and Editing'
              : 'Basic Page Navigation'
          }
          className="font-bold text-xl text-red-600"
        />
      ),
    },
    {
      content: <ViewingData />,
      title: <CustomTitleBar title="Viewing Data" className="font-bold text-xl text-red-600" />,
    },
    {
      content: <TransactionDetails isImportable={isImportable} />,
      title: (
        <CustomTitleBar
          title="Transaction Details/Export"
          className="font-bold text-xl text-red-600"
        />
      ),
    },
  ];
};

export default useGuideMeSteps;
